import { api } from './api';
import { canAccess, ROLE_TIER_1, ROLE_TIER_1_ADMIN, ROLE_USER, type Feature, type Role } from './roles';

let cached: Promise<string | null> | null = null;
let cachedRole: Role | null = null;
let cachedMe: { nickname?: string; telegramLinked?: boolean; telegramAvailable?: boolean } = {};

// Підказка про статус входу, що переживає перезавантаження сторінки. Відповідь /api/me
// приходить асинхронно, а меню (nav.ts) малюється одразу, ще до неї: без підказки гість
// на мить бачив пункти Бібліотека/Нове/Історія, які потім зникали. Із підказкою меню
// з першого кадру має правильний вигляд; справжня відповідь сервера її лише оновлює.
const HINT_KEY = 'lnr-auth-hint';

export function getAuthHint(): 'user' | 'guest' | null {
  try {
    const v = localStorage.getItem(HINT_KEY);
    return v === 'user' || v === 'guest' ? v : null;
  } catch {
    return null;
  }
}

// Роль так само запам'ятовуємо підказкою — щоб меню (Каталог замість Огляду для tier_1) було правильним
// з першого кадру. "user"/"guest" у HINT_KEY означає лише "залогінений чи ні" (гостьовий акаунт теж "user").
const ROLE_HINT_KEY = 'lnr-role-hint';

export function getRoleHint(): Role | null {
  try {
    const v = localStorage.getItem(ROLE_HINT_KEY);
    return v === ROLE_USER || v === ROLE_TIER_1 || v === ROLE_TIER_1_ADMIN ? v : null;
  } catch {
    return null;
  }
}

export function setAuthHint(username: string | null, role?: string | null): void {
  try {
    localStorage.setItem(HINT_KEY, username ? 'user' : 'guest');
    if (!username) localStorage.removeItem(ROLE_HINT_KEY);
    else if (role === ROLE_USER || role === ROLE_TIER_1 || role === ROLE_TIER_1_ADMIN) localStorage.setItem(ROLE_HINT_KEY, role);
  } catch {
    /* сховище недоступне (приватний режим тощо) — підказка просто не діятиме */
  }
}

/** Чи дозволена функція за ЗАПАМ'ЯТОВАНОЮ роллю (синхронно, для першого малювання; точний варіант — після getCurrentRole). */
export function canAccessByHint(feature: Feature): boolean {
  return canAccess(getRoleHint() ?? (getAuthHint() === 'user' ? ROLE_USER : null), feature);
}

/** Хто зараз залогінений (кешується на час життя сторінки — один запит на завантаження). */
export function getCurrentUser(): Promise<string | null> {
  if (!cached) {
    cached = api
      .me()
      .then((r) => {
        cachedRole = r.role === ROLE_TIER_1_ADMIN ? ROLE_TIER_1_ADMIN : r.role === ROLE_TIER_1 ? ROLE_TIER_1 : ROLE_USER;
        cachedMe = { nickname: r.nickname, telegramLinked: r.telegramLinked, telegramAvailable: r.telegramAvailable };
        return r.username;
      })
      .catch(() => null)
      .then((username) => {
        if (!username) cachedRole = null;
        setAuthHint(username, cachedRole);
        return username;
      });
  }
  return cached;
}

/**
 * Для сторінок, доступних лише авторизованим (Бібліотека/Нове/Історія):
 * якщо входу немає — перекидає і повертає null. Викликати першим рядком у init-функції такої сторінки.
 *
 * Куди саме:
 *  - гість (ніколи не входив) → Каталог (/) — саме з нього має відкриватись PWA (start_url —
 *    /library.html), а не з "Більше";
 *  - той, хто раніше був залогінений, а сесія спливла (підказка 'user' ще з минулого візиту) →
 *    "Більше", де форма входу.
 * location.replace — щоб сторінка, яка одразу перекидає, не лишалась в історії (інакше "назад"
 * повертає на неї, і вона знову перекидає).
 */
export async function requireAuthOrRedirect(): Promise<string | null> {
  const wasLoggedIn = getAuthHint() === 'user'; // читаємо ДО getCurrentUser — він одразу оновить підказку
  const username = await getCurrentUser();
  if (!username) {
    location.replace(wasLoggedIn ? '/more' : '/');
    return null;
  }
  return username;
}

/** Роль поточного користувача (null — гість без акаунта). */
export async function getCurrentRole(): Promise<Role | null> {
  await getCurrentUser();
  return cachedRole;
}

/** Ім'я для показу: нікнейм гостьового акаунта (його username — внутрішній id), для решти — логін. */
export async function getDisplayName(): Promise<string | null> {
  const username = await getCurrentUser();
  return username ? cachedMe.nickname ?? username : null;
}

/** Стан прив'язки Telegram (лише для гостьового акаунта; з відповіді /api/auth/me). */
export async function getTelegramState(): Promise<{ linked: boolean; available: boolean }> {
  await getCurrentUser();
  return { linked: !!cachedMe.telegramLinked, available: !!cachedMe.telegramAvailable };
}

/**
 * Для сторінок, недоступних гостьовому акаунту (друзі, рекомендації…): не авторизований — як
 * requireAuthOrRedirect, роль без права — на Каталог. Сервер у будь-якому разі віддасть 403.
 */
export async function requireFeatureOrRedirect(feature: Feature): Promise<string | null> {
  const username = await requireAuthOrRedirect();
  if (!username) return null;
  if (!canAccess(await getCurrentRole(), feature)) {
    location.replace('/');
    return null;
  }
  return username;
}
